import Debugger from './Classes/Debugger';
import Interface from './Classes/Interface';
import Translator from './Classes/Translator';
import Tag from './Classes/Tag';
import DefaultTag from './Classes/Tag/DefaultTag';
import GoogleTag from './Classes/Tag/GoogleTag';
import IFrameTag from './Classes/Tag/IFrameTag';
import ScriptTag from './Classes/Tag/ScriptTag';
import StyleTag from './Classes/Tag/StyleTag';

import Settings from "./Interfaces/Settings";
import Tags from "./Interfaces/Tags";

/**
 * ConsentManager
 *
 * @author Felix Kiesewetter <info@felixkiesewetter.de>
 * @package felixkiesewetter-js/consentmanager
 * @version 5.2.0
 * @lastModified 2025-07-11
 */
class ConsentManager
{

    #debugger :Debugger = (new Debugger).setIdentifier(this.constructor.name);
    #errors :Object = {};
    #interface :Interface;
    #isConstructed :Boolean = false;
    #settings :Settings = {
        debug: false,
        disableFrames: true,
        interfaceTemplate: '<p class="consent-manager__header">{interface_header}</p><p class="consent-manager__text">{interface_text}</p>',
        interfaceTheme: ConsentManager.THEME_LIGHT,
        interfaceLayout: ConsentManager.LAYOUT_BOTTOM_LEFT,
        lifetime: 24 * 3600 * 1000,
        fingerprintIcon: true,
        fingerprintIconLayout: ConsentManager.FINGERPRINT_LAYOUT_BOTTOM_RIGHT,
        privacyUri: '/datenschutz',
        queuePageReloadDelay: 1000,
        useAcceptAll: true,
        useDenyAll: false
    };
    #queuePageReloadTimeout = null;
    #requireUserInteraction = false;
    #storageIdentifier = 'ConsentManagerStorage';
    #tags :Tags = {};
    #translator;

    /**
     * ConsentManager constructor
     *
     * @param {object} options
     * @param {array} tags
     * @param {object} translations
     * @returns {ConsentManager}
     */
    constructor(options :object, tags :Tag[] = [], translations :object)
    {
        if (!this.#validateOptions(options)) {
            return this;
        }
        this.#mergeSettings(options)
            .#loadTranslator(translations)
            .#loadInterface()
            .#addInitialTags(tags)
            .#addEventHandlers();
        if (this.#requireUserInteraction) {
            this.showInterface();
        }
        this.#isConstructed = true;
        return this;
    }

    /**
     * Validate options
     *
     * @param {Object} options
     * @returns {boolean}
     */
    #validateOptions(options)
    {
        if (!options) {
            console.error('[ConsentManager]', 'No options argument passed to constructor')
            return false
        }
        // TODO: validate more options
        return true
    }

    /**
     * Merge options with default settings
     *
     * @param options
     * @private
     */
    #mergeSettings(options :Object) :ConsentManager
    {
        const invalidOptions = [];
        Object.keys(options).forEach(option => {
            if ('undefined' === typeof this.#settings[option]) {
                invalidOptions.push(option);
                delete options[option];
            }
        });
        this.#settings = { ...this.#settings, ...options };
        if (!this.#settings.debug) {
            this.#debugger.disable();
        }
        if (invalidOptions.length) {
            this.#debugger.error('Invalid options:', invalidOptions);
        }
        this.#debugger.log(this.#settings);
        return this;
    }

    /**
     * Load Translator instance
     *
     * @param translations
     * @private
     */
    #loadTranslator(translations :object) :ConsentManager
    {
        this.#translator = new Translator(translations);
        return this;
    }

    /**
     * Build consent manager interface
     *
     * @returns {ConsentManager}
     */
    #loadInterface() :ConsentManager
    {
        this.#interface = new Interface(this);
        return this;
    }

    /**
     * Add the initial tags (tags passed to constructor)
     *
     * @param {Array} tags
     * @returns {ConsentManager}
     */
    #addInitialTags(tags)
    {
        this.addTag(new DefaultTag({
            label: this.#translator.get('tag_default'),
        }), true);
        if (tags.length) {
            tags.forEach(tag => {
                this.addTag(tag, true);
            });
        }
        else if (!Object.values(this.getStorage()).length) {
            this.setRequireUserInteraction(true);
        }
        this.#debugger.log(this.#tags);
        return this;
    }

    /**
     * Add event handlers to buttons and components
     *
     * @private
     */
    #addEventHandlers() :ConsentManager
    {
        this.#interface.getAcceptAll().addEventListener('click', (e :PointerEvent) :void => {
            e.stopPropagation();
            e.preventDefault();
            this.#updateTags(true);
        });

        this.#interface.getAcceptSelected().addEventListener('click', (e :PointerEvent) :void => {
            e.stopPropagation();
            e.preventDefault();
            this.#updateTags(false);
        });

        this.#interface.getDenyAll().addEventListener('click', (e :PointerEvent) :void => {
            e.stopPropagation();
            e.preventDefault();
            this.#updateTags(false, true);
        });

        for (const trigger of document.querySelectorAll('[rel="consent-manager"]')) {
            trigger.addEventListener('click', (e) => {
                e.preventDefault();
                this.showInterface(trigger.dataset.consent);
            });
        }

        window.addEventListener('click', (e) => {
            if ('consent-manager' !== e.target.rel) {
                return true;
            }
            if (null !== e.target.parentNode && 'consent-manager' !== e.target.parentNode.rel) {
                return true;
            }
            e.preventDefault();
            this.showInterface(e.target.dataset.consent);
        });
    }

    /**
     * Update tags
     *
     * @param acceptAll
     * @param denyAll
     */
    #updateTags(acceptAll :Boolean = false, denyAll :Boolean = false) :void
    {
        const checkboxes = this.#interface.getNode().querySelectorAll('input[type="checkbox"][name]:not([readonly])');
        let storeConsent = {};
        if (acceptAll) {
            for (const checkbox of checkboxes) {
                checkbox.checked = true;
            }
        }
        else if (true === denyAll) {
            for (const checkbox of checkboxes) {
                checkbox.checked = false;
            }
        }
        for (const checkbox of checkboxes) {
            const tagName = checkbox.name.split('[')[1].split(']')[0]; // TODO: instead of a name="" attribute, just use dataset
            const tag = this.#tags[tagName];
            storeConsent[tagName] = false;
            if (checkbox.checked) {
                storeConsent[tagName] = true;
                if (!tag.getConsent()) {
                    tag.allow();
                }
            }
            else if (!checkbox.checked && tag.getConsent()) {
                tag.disallow();
            }
        }
        try {
            const storeData = {
                consent: storeConsent,
                timestamp: Date.now().toString()
            };
            localStorage.setItem(this.#storageIdentifier, JSON.stringify(storeData));
            this.#settings.debug && console.log('ConsentManager.#updatePropertes()', storeData);
        }
        catch (error) {
            this.#errors.push(error);
        }
        this.hideInterface();
        this.#requireUserInteraction = false;
        window.dispatchEvent(new CustomEvent('consentSettingsUpdated'));
    }

    /**
     * Add tag to consent manager
     *
     * @param {Tag} tag
     * @param {boolean} onInitialisation
     * @returns {ConsentManager}
     */
    addTag(tag, onInitialisation = false)
    {
        tag.init(this || null);
        // TODO: check if tag with name exists already
        this.#tags[tag.getName()] = tag;
        this.#debugger.log(tag);
        this.#interface.addTag(tag);
        return this;
    }

    /**
     * Await window.ConsentManager
     *
     * @param callback
     */
    static await(callback)
    {
        if (!callback || Function !== callback.constructor) {
            return;
        }
        const waitForConsentManager = setInterval(() => {
            // TODO: What if the globally set ConsentManager is NOT stored in window.ConsentManager??
            // TODO: Either force window.ConsentManager on initialisation (constructor()) or make it dynamic via func arg
            if (window.ConsentManager) {
                clearInterval(waitForConsentManager);
                callback(window.ConsentManager);
            }
        }, 250);
    }

    /**
     * Register callback function for a tag
     *
     * The callback function will also be called right away
     *
     * @param {String} tag
     * @param {function|null} callback
     * @returns {ConsentManager}
     */
    registerTagCallback(tag, callback = null)
    {
        if (!tag || 0 > Object.keys(this.#tags).indexOf(tag)) {
            console.warn('ConsentManager.registerTagCallback()', `tag "${tag}" does not exist.`); // TODO: use Debugger
            return this;
        }
        if (!callback || Function !== callback.constructor) {
            console.warn('ConsentManager.registerTagCallback()', `Callback "${callback}" is not a function.`); // TODO: use Debugger
            return this;
        }
        this.#tags[tag].setCallback(callback);
        this.#settings.debug && console.log('ConsentManager.registerTagCallback()', tag, callback);
        callback(this.getTagConsent(tag));
        return this;
    }

    /**
     * Show interface and, optionally, highlight a tag
     *
     * @param highlightTagWithName
     */
    showInterface(highlightTagWithName :null|String = null)
    {
        this.#interface.show();
        if (!highlightTagWithName) {
            return this;
        }
        this.#interface.highlightTagWithName(highlightTagWithName);
        return this;
    }

    /**
     * Hide interface
     */
    hideInterface() :ConsentManager
    {
        this.#interface.hide();
        return this;
    }

    /**
     * Check if the current instance only uses the default tag (required cookies)
     *
     * @returns {boolean}
     */
    usesOnlyDefaultTag()
    {
        return 1 === Object.keys(this.#tags).length && 'default' === Object.keys(this.#tags)[0];
    }

    // TODO: move to ConsentManager.StyleTag
    enableStyleTag(tagName)
    {
        const styleTags = document.querySelectorAll(`style[type="text/plain"][data-consent="${tagName}"], link[type="text/plain"][data-consent="${tagName}"]`);
        if (styleTags) {
            styleTags.forEach((styleTag) => {
                styleTag.parentNode.removeChild(styleTag);
                styleTag.setAttribute('type', 'text/css');
                if (styleTag.hasAttribute('data-src')) {
                    styleTag.setAttribute('src', styleTag.getAttribute('data-src'));
                    styleTag.removeAttribute('data-src');
                }
                if (styleTag.hasAttribute('data-href')) {
                    styleTag.setAttribute('href', styleTag.getAttribute('data-href'));
                    styleTag.removeAttribute('data-href');
                }
                document.head.appendChild(styleTag);
                const styleTagName = styleTag.tagName.toLowerCase();
                this.#settings.debug && console.log(`ConsentManager.#enablestyleTag(${tagName})`, `Enabled \<${styleTagName}\> with attribute data-consent="${tagName}"`);
            });
        }
        else {
            this.#settings.debug && console.warn(`ConsentManager.#enablestyleTag(${tagName})`, `There is no style tag with attribute data-consent="${tagName}"`);
        }
    }

    // TODO: move to ConsentManager.IFrameTag
    enableIFrame(tagName)
    {
        const iFrames = document.querySelectorAll(`iframe[data-src][data-consent="${tagName}"]`);
        if (iFrames) {
            iFrames.forEach((iFrame) => {
                iFrame.src = iFrame.dataset.src;
                iFrame.removeAttribute('data-src');
            });
        }
        else {
            this.#settings.debug && console.warn(`ConsentManager.#enableIFrame(${tagName})`, `There is no iframe with attribute data-consent="${tagName}"`);
        }
    }

    queuePageReload()
    {
        if (null === this.#queuePageReloadTimeout) {
            this.#settings.debug && console.warn(`ConsentManager.queuePageReload()`);
            let c = confirm(this.#translator.get('notice_page_reload'));
            if (c) {
                this.#queuePageReloadTimeout = setTimeout(() => {
                    window.location.reload();
                }, this.#settings.queuePageReloadDelay);
            }
        }
    }

    // #######
    // Getters
    // #######

    /**
     * Get cookie domain without subdomain with leading .
     *
     * @returns {String}
     */
    public getCookieDomain()
    {
        return `.${window.location.hostname.replace(/^[^.]+\./g, '')}`;
    }

    /**
     * Getter for the debugger utility
     *
     * @returns {Debugger}
     */
    public getDebugger()
    {
        return this.#debugger;
    }

    /**
     * Get privacy uri
     *
     * @returns {String}
     */
    public getPrivacyUri()
    {
        if (this.#settings.privacyUri.constructor === Object) {
            return this.#settings.privacyUri[this.#translator.getLanguage()] || '/';
        }
        return this.#settings.privacyUri;
    }

    /**
     * Get a certain setting
     *
     * @param settingKey
     */
    public getSetting(settingKey :string) :null|String|Object
    {
        return this.#settings[settingKey] || null;
    }

    /**
     * Get (local) storage and check if saved settings are still valid
     *
     * @returns {Object}
     */
    public getStorage()
    {
        const storage = JSON.parse(localStorage.getItem(this.#storageIdentifier)) || {};
        if (
            Object !== storage.constructor ||
            !storage.hasOwnProperty('consent') ||
            !storage.hasOwnProperty('timestamp') ||
            Date.now() > (parseInt(storage.timestamp)) + (this.#settings.lifetime)
        ) {
            this.setRequireUserInteraction(true);
            return {};
        }
        return storage;
    }

    /**
     * Get the consent status of tag by its name
     *
     * @param {string} tagName
     * @returns {boolean}
     */
    public getTagConsent(tagName)
    {
        if (!(tagName in this.#tags)) {
            console.warn('ConsentManager.#getTagConsent', `tag "${tagName}" does not exist`); // TODO: use Debugger
            return false;
        }
        return this.#tags[tagName].getTagConsent();
    }

    /**
     * Get all active tags.
     */
    public getTags() :Tags
    {
        return this.#tags;
    }

    /**
     * Getter for the Translator instance
     *
     * @returns {Translator}
     */
    public getTranslator() :Translator
    {
        return this.#translator;
    }

    // #######
    // Setters
    // #######

    /**
     * Set whether the consent manager requires an interaction
     *
     * @param {boolean} requireUserInteraction
     * @returns {ConsentManager}
     */
    setRequireUserInteraction(requireUserInteraction)
    {
        this.#requireUserInteraction = requireUserInteraction;
        if (this.#isConstructed && requireUserInteraction) {
            this.showInterface();
        }
        return this;
    }

    /**
     * Set the consent status for a tag by its name
     *
     * @param {string} tagName
     * @param {boolean} allow
     * @param {boolean} reload
     * @returns {ConsentManager}
     */
    setTagConsent(tagName, allow = false, reload = false)
    {
        if (!(tagName in this.#tags)) {
            console.warn('ConsentManager.#setTagConsent', `tag "${tagName}" does not exist`); // TODO: use Debugger
            return this;
        }
        this.#settings.debug && console.log('ConsentManager.#setTagConsent', 'setTagConsent()', arguments); // TODO: use Debugger
        if (allow) {
            this.#tags[tagName].allow();
        }
        else {
            this.#tags[tagName].disallow();
        }
        // TODO: Requires an update of local storage
        // TODO: Also, does not seem to work ...
        window.dispatchEvent(new CustomEvent('consentSettingsUpdated'));
        if (reload) {
            this.queuePageReload();
        }
        return this;
    }

    // #########
    // Constants
    // #########

    static THEME_LIGHT = 'theme-light';
    static THEME_DARK = 'theme-dark';

    static LAYOUT_CENTER = 'center';
    static LAYOUT_BOTTOM_LEFT = 'bottom-left';
    static LAYOUT_BOTTOM_WIDE = 'bottom-wide';
    static LAYOUT_BOTTOM_RIGHT = 'bottom-right';

    static FINGERPRINT_LAYOUT_BOTTOM_LEFT = 'bottom-left';
    static FINGERPRINT_LAYOUT_BOTTOM_RIGHT = 'bottom-right';

    static LAYOUT_TOP_LEFT = 'top-left';
    static LAYOUT_TOP_WIDE = 'top-wide';
    static LAYOUT_TOP_RIGHT = 'top-right';
}

// Define namespaces
ConsentManager.Tag = Tag;
ConsentManager.Tag.GoogleTag = GoogleTag;
ConsentManager.Tag.IFrameTag = IFrameTag;
ConsentManager.Tag.ScriptTag = ScriptTag;
ConsentManager.Tag.StyleTag = StyleTag;

// Export
export default ConsentManager;
